import { lazy, Suspense, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import { usePublicGuestQuestion } from '../hooks/usePublicGuestQuestion';
import { FadeSwitch } from './FadeSwitch';
import { LandingGuestQuestionComposer } from './LandingGuestQuestionComposer';
import { LandingGuestQuestionAnswer } from './LandingGuestQuestionAnswer';
import { LandingGuestQuestionError } from './LandingGuestQuestionError';
import { LandingGuestQuestionWaiting } from './LandingGuestQuestionWaiting';

const DevelopmentPreview = import.meta.env.DEV ? lazy(() => import('./LandingGuestQuestionPreview')) : null;
type LandingGuestQuestionProps = { onGetStarted: () => void; onSignIn: () => void };

export function LandingGuestQuestion(props: LandingGuestQuestionProps) {
  const previewEnabled = import.meta.env.DEV && typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('heroPreview') === '1';
  if (DevelopmentPreview && previewEnabled) return <Suspense fallback={null}><DevelopmentPreview /></Suspense>;
  return <LandingGuestQuestionLive {...props} />;
}

function LandingGuestQuestionLive({ onGetStarted, onSignIn }: LandingGuestQuestionProps) {
  const { t, language } = useTranslation();
  const guest = usePublicGuestQuestion({ onGetStarted, onSignIn });
  const answerRegionRef = useRef<HTMLDivElement>(null);
  const hasAnswer = Boolean(guest.receipt);
  const isWaiting = guest.phase === 'waiting';
  const hasError = !hasAnswer && (guest.phase === 'error' || (guest.botProtection.hasError && !guest.isBusy));
  const errorKey = guest.errorKind === 'save' ? 'saveError'
    : guest.errorKind === 'protection' || guest.botProtection.hasError ? 'protectionError' : 'requestError';

  useEffect(() => {
    if (!hasAnswer) return;
    const frame = window.requestAnimationFrame(() => {
      const input = answerRegionRef.current?.querySelector<HTMLInputElement>('#guest-answer-email');
      if (!input) return;
      const { top, bottom } = input.getBoundingClientRect();
      if (top >= 0 && bottom <= window.innerHeight) input.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [hasAnswer]);

  return (
    <section aria-labelledby={hasAnswer ? 'landing-guest-answer-title' : isWaiting ? 'guest-question-waiting-title' : 'landing-guest-title'} data-landing-guest-question className="landing-guest-question relative isolate mx-auto w-full max-w-xl text-left lg:max-w-none">
      <div ref={answerRegionRef} className="relative grid min-w-0 grid-cols-1">
        <GuestQuestionStage active={!hasAnswer && !isWaiting}>
          <LandingGuestQuestionComposer
            question={guest.question} website={guest.website} phase={guest.phase} isBusy={guest.isBusy}
            captchaContainerRef={guest.botProtection.captchaContainerRef} captchaEnabled={guest.botProtection.isEnabled && !guest.botProtection.isVerified}
            onQuestionChange={guest.setQuestion} onExampleSelect={guest.selectExample} onWebsiteChange={guest.setWebsite} onSubmit={() => { void guest.submit(); }}
          />
        </GuestQuestionStage>
        <GuestQuestionStage active={isWaiting}>
          <LandingGuestQuestionWaiting isOpen={isWaiting} />
        </GuestQuestionStage>
        <GuestQuestionStage active={hasAnswer}>
          {guest.receipt && <LandingGuestQuestionAnswer receipt={guest.receipt} locale={guest.submittedQuestion?.language ?? language} />}
        </GuestQuestionStage>
      </div>
      <span className="sr-only" role="status" aria-live="polite">{guest.phase === 'verifying' ? t('presentation.guestQuestion.verifying') : guest.phase === 'complete' ? t('presentation.guestQuestion.answerComplete') : ''}</span>
      <FadeSwitch viewKey={hasError ? errorKey : 'none'}>
        {hasError && <LandingGuestQuestionError errorKey={errorKey} onGetStarted={onGetStarted} onRetry={() => { void guest.submit(); }} />}
      </FadeSwitch>
    </section>
  );
}

function GuestQuestionStage({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <div
      aria-hidden={active ? undefined : true}
      {...(!active ? { inert: '' } : {})}
      className={`min-w-0 max-w-full col-start-1 row-start-1 transition-[opacity,visibility] [transition-duration:700ms,0s] ease-out motion-reduce:transition-none ${active ? 'relative visible opacity-100 [transition-delay:0ms,0ms]' : 'pointer-events-none invisible absolute inset-0 opacity-0 [transition-delay:0ms,700ms]'}`}
    >
      {children}
    </div>
  );
}
